import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Lock, CircleNotch, SignOut } from "@phosphor-icons/react";

// After this much inactivity, lock the screen and require the password again.
// Testing override: set window.__IDLE_TEST_MS in the console to use a shorter window.
const IDLE_MS = 60 * 60 * 1000; // 60 minutes
const HEARTBEAT_MS = 2 * 60 * 1000; // 2 minutes

export default function SessionGuard() {
  const { user, logout } = useAuth();
  const [locked, setLocked] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const idleTimer = useRef(null);

  const idleWindow = () => window.__IDLE_TEST_MS || IDLE_MS;

  const resetIdle = useCallback(() => {
    if (locked) return;
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setLocked(true), idleWindow());
  }, [locked]);

  // Activity listeners
  useEffect(() => {
    if (!user || user === false) return;
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetIdle, { passive: true }));
    resetIdle();
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetIdle));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [user, resetIdle]);

  // Heartbeat to keep session "time on app" accurate (paused while locked/hidden)
  useEffect(() => {
    if (!user || user === false) return;
    const beat = () => { if (!document.hidden && !locked) api.post("/activity/heartbeat").catch(() => {}); };
    const id = setInterval(beat, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [user, locked]);

  const unlock = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reverify", { password });
      setPassword("");
      setLocked(false);
      resetIdle();
    } catch (err) {
      setError(err.response?.data?.detail || "Incorrect password. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  if (!locked || !user || user === false) return null;

  const isPasswordUser = (user.auth_provider || "password") === "password";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A0A0A]/90 backdrop-blur-md p-4" data-testid="idle-lock-overlay">
      <div className="w-full max-w-sm rounded-sm border border-neutral-700 bg-white p-7 shadow-2xl">
        <div className="mb-5 flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-sm bg-[#0A0A0A] text-white">
            <Lock size={24} weight="fill" />
          </div>
          <h2 className="font-heading text-2xl font-black tracking-tighter text-[#0A0A0A]">Session locked</h2>
          <p className="mt-1 text-sm text-neutral-500">
            You were inactive for a while. {isPasswordUser ? "Re-enter your password to continue." : "Please sign in again to continue."}
          </p>
          <p className="mt-2 text-xs font-semibold text-neutral-400">{user.email}</p>
        </div>

        {isPasswordUser ? (
          <form onSubmit={unlock} className="space-y-3" data-testid="idle-lock-form">
            <input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              data-testid="idle-lock-password"
              className="w-full rounded-sm border border-neutral-300 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#0055FF]"
            />
            {error && <p className="text-sm text-[#DC2626]" data-testid="idle-lock-error">{error}</p>}
            <button
              type="submit"
              disabled={busy || !password}
              data-testid="idle-lock-submit"
              className="flex w-full items-center justify-center gap-2 rounded-sm bg-[#0055FF] px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#0040cc] disabled:opacity-50"
            >
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <Lock size={16} weight="bold" />} Unlock
            </button>
            <button type="button" onClick={handleLogout} data-testid="idle-lock-logout" className="w-full text-center text-xs font-semibold text-neutral-500 hover:text-[#0055FF]">
              Sign out instead
            </button>
          </form>
        ) : (
          <button onClick={handleLogout} data-testid="idle-lock-logout" className="flex w-full items-center justify-center gap-2 rounded-sm bg-[#0055FF] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0040cc]">
            <SignOut size={16} weight="bold" /> Sign in again
          </button>
        )}
      </div>
    </div>
  );
}
